import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useEffect } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const bg = '#f0ebe3';
const red = '#6B1A1A';
const dark = '#4a1010';
const serif = 'Playfair Display, serif';
const body = 'Cormorant Garamond, serif';

function Services() {
  const isMobile = useIsMobile();

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Cormorant+Garamond:wght@300;400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const services = [
    {
      title: 'AI Voice Agents',
      tag: 'NEVER MISS A LEAD',
      problem: 'Every missed call is a missed opportunity. Most businesses lose leads after hours, on weekends, and whenever they are too busy to pick up.',
      description: 'Our AI voice agents answer every call, qualify every lead, and book every appointment — 24 hours a day, 7 days a week. Inbound and outbound. No voicemail. No missed revenue.',
      includes: [
        '24/7 inbound AI receptionist',
        'Outbound follow-up calling',
        'Missed call text-back',
        'Appointment booking via phone',
        'Live transfer to human staff',
      ],
    },
    {
      title: 'CRM & Pipeline Management',
      tag: 'STAY ORGANIZED',
      problem: 'Leads fall through the cracks when there is no system. Manual follow-up is inconsistent and slow — and slow follow-up loses jobs.',
      description: 'A fully built CRM that organizes your leads, tracks every deal, and follows up automatically. No spreadsheets. No sticky notes. No manual data entry.',
      includes: [
        'CRM setup & buildout',
        'Lead pipeline management',
        'Automated text, email & voicemail sequences',
        'Deal stage automation',
        'Contact tagging & segmentation',
      ],
    },
    {
      title: 'Workflow Automation',
      tag: 'RUN ON AUTOPILOT',
      problem: 'Repetitive tasks eat your time. From lead intake to post-job follow-up, most of your admin work can be eliminated entirely.',
      description: 'Every repetitive task in your business — handled automatically. From the moment a lead comes in to the moment the job is done, your operation runs itself.',
      includes: [
        'New lead intake automation',
        'Appointment reminder sequences',
        'Post-job follow-up workflows',
        'Cancellation & reschedule handling',
        'Internal staff notifications & task triggers',
      ],
    },
    {
      title: 'Reputation Management',
      tag: 'BUILD YOUR NAME',
      problem: 'Most businesses do great work but never ask for reviews. Your competitors with more reviews are winning jobs you should be getting.',
      description: 'More 5-star reviews, less effort. We automate the entire review process so your reputation builds itself — without you having to think about it.',
      includes: [
        'Automated review requests after jobs',
        'Review response automation',
        'Google Business Profile management',
      ],
    },
    {
      title: 'Custom AI Agents',
      tag: 'BUILT FOR YOUR BUSINESS',
      problem: 'Every business is different. Off-the-shelf tools were not built for how you operate — and it shows.',
      description: 'Need something specific? We build task-specific AI agents tailored to exactly how your business runs. From client intake to staff coordination, we build the agent your operation actually needs.',
      includes: [
        'Staff coordination & project management agents',
        'Scheduling & ops agents',
        'Client intake agents',
        'AI chat agents for your website',
        'Document & form processing agents',
      ],
    },
  ];

  const cardContent = (service, i) => (
    <div key={i} style={{ backgroundColor: bg, padding: isMobile ? '30px 24px' : '40px' }}>
      <p style={{ color: red, fontSize: '10px', letterSpacing: '3px', margin: '0 0 12px' }}>{service.tag}</p>
      <h3 style={{ fontFamily: serif, fontSize: isMobile ? '24px' : '28px', fontWeight: '700', color: dark, margin: '0 0 16px' }}>{service.title}</h3>
      <p style={{ fontSize: '14px', color: red, fontStyle: 'italic', margin: '0 0 16px', lineHeight: '1.6' }}>{service.problem}</p>
      <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', margin: '0 0 24px', fontWeight: '300' }}>{service.description}</p>
      <div style={{ borderTop: '0.5px solid #d4c9bc', paddingTop: '20px', marginBottom: '24px' }}>
        {service.includes.map((item, j) => (
          <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
            <div style={{ width: '4px', height: '4px', backgroundColor: red, transform: 'rotate(45deg)', flexShrink: 0, marginTop: '6px' }}></div>
            <span style={{ fontSize: '14px', color: '#444', fontWeight: '300' }}>{item}</span>
          </div>
        ))}
      </div>
      <a href="/contact" style={{ display: 'inline-block', backgroundColor: red, color: bg, padding: '12px 24px', textDecoration: 'none', fontSize: '10px', letterSpacing: '2px' }}>
        GET STARTED
      </a>
    </div>
  );

  return (
    <div style={{ backgroundColor: bg, minHeight: '100vh', fontFamily: body, color: '#2a1a1a' }}>
      <Navbar />
      <div style={{ textAlign: 'center', padding: isMobile ? '40px 24px 20px' : '60px 40px 20px' }}>
        <p style={{ color: red, fontSize: '11px', letterSpacing: '4px', margin: '0 0 16px' }}>AI CONSULTATION</p>
        <h2 style={{ fontFamily: serif, fontSize: isMobile ? '36px' : '48px', fontWeight: '700', color: dark, margin: '0 0 16px', lineHeight: '1' }}>Our Services</h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', margin: '20px 0' }}>
          <div style={{ height: '1px', width: '80px', backgroundColor: '#c9a882' }}></div>
          <div style={{ width: '6px', height: '6px', backgroundColor: '#c9a882', transform: 'rotate(45deg)' }}></div>
          <div style={{ height: '1px', width: '80px', backgroundColor: '#c9a882' }}></div>
        </div>
        <p style={{ fontSize: '16px', color: '#555', maxWidth: '520px', margin: '0 auto', lineHeight: '1.8', fontWeight: '300' }}>
          We help local businesses grow with AI. Five services. Every tool you need to answer faster, follow up better, and operate smarter.
        </p>
      </div>

      {/* Top 4 cards in 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1px', backgroundColor: '#d4c9bc', margin: isMobile ? '30px 24px 1px' : '40px 60px 1px', border: '1px solid #d4c9bc' }}>
        {services.slice(0, 4).map((service, i) => cardContent(service, i))}
      </div>

      {/* 5th card centered */}
<div style={{ display: 'flex', justifyContent: 'center', backgroundColor: bg, margin: isMobile ? '0 24px 60px' : '0 60px 80px', borderLeft: '1px solid #d4c9bc', borderRight: '1px solid #d4c9bc', borderBottom: '1px solid #d4c9bc' }}>
        <div style={{ width: isMobile ? '100%' : '50%' }}>
          {cardContent(services[4], 4)}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Services;